import React from 'react';
import useClockTime from '../useClockTime';

const CX = 150, CY = 150;

// Only 12 hour markers (dots at 5-min positions, longer lines at quarters)
const hourMarkers = Array.from({ length: 12 }, (_, i) => {
  const isQuarter = i % 3 === 0;
  const angle = (i * 30 - 90) * Math.PI / 180;
  const outerR = 128;
  const innerR = isQuarter ? outerR - 16 : outerR - 9;
  return {
    x1: CX + outerR * Math.cos(angle),
    y1: CY + outerR * Math.sin(angle),
    x2: CX + innerR * Math.cos(angle),
    y2: CY + innerR * Math.sin(angle),
    strokeWidth: isQuarter ? 3 : 1.5,
  };
});

// 60 minute dots (very small)
const minuteDots = Array.from({ length: 60 }, (_, i) => {
  if (i % 5 === 0) return null; // skip hour positions
  const angle = (i * 6 - 90) * Math.PI / 180;
  const r = 128;
  return { cx: CX + r * Math.cos(angle), cy: CY + r * Math.sin(angle) };
}).filter(Boolean);

const DarkNavy = () => {
  const { hourDeg, minuteDeg, secondDeg } = useClockTime();

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 300, height: 300 }}>
      <svg width={300} height={300} viewBox="0 0 300 300" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <radialGradient id="dn-face" cx="40%" cy="35%">
            <stop offset="0%" stopColor="#0f1e35" />
            <stop offset="100%" stopColor="#060e1e" />
          </radialGradient>
          <radialGradient id="dn-outer" cx="50%" cy="50%">
            <stop offset="0%" stopColor="#0d1a30" />
            <stop offset="100%" stopColor="#050c18" />
          </radialGradient>
        </defs>

        {/* Outer background */}
        <circle cx={CX} cy={CY} r="148" fill="url(#dn-outer)" />

        {/* Outer rings — thin gold */}
        <circle cx={CX} cy={CY} r="148" fill="none" stroke="#c8a84b" strokeWidth="1.5" />
        <circle cx={CX} cy={CY} r="143" fill="none" stroke="#c8a84b" strokeWidth="0.5" opacity="0.5" />

        {/* Face */}
        <circle cx={CX} cy={CY} r="136" fill="url(#dn-face)" />
        <circle cx={CX} cy={CY} r="136" fill="none" stroke="#c8a84b" strokeWidth="1.2" />
        <circle cx={CX} cy={CY} r="132" fill="none" stroke="#c8a84b" strokeWidth="0.4" opacity="0.4" />

        {/* Minute dots */}
        {minuteDots.map((d, i) => (
          <circle key={i} cx={d.cx} cy={d.cy} r="1.2" fill="#c8a84b" opacity="0.5" />
        ))}

        {/* Hour markers */}
        {hourMarkers.map((m, i) => (
          <line key={i} x1={m.x1} y1={m.y1} x2={m.x2} y2={m.y2}
            stroke="#c8a84b" strokeWidth={m.strokeWidth} strokeLinecap="round" />
        ))}

        {/* Large dots at 12, 3, 6, 9 */}
        {[0, 90, 180, 270].map((deg, i) => {
          const angle = (deg - 90) * Math.PI / 180;
          const r = 112;
          return (
            <circle key={i}
              cx={CX + r * Math.cos(angle)}
              cy={CY + r * Math.sin(angle)}
              r="3.5" fill="#c8a84b" />
          );
        })}

        {/* Hour hand — slim tapered */}
        <g transform={`rotate(${hourDeg}, ${CX}, ${CY})`}>
          <polygon
            points={`${CX - 3},${CY + 18} ${CX + 3},${CY + 18} ${CX + 1.5},${CY - 72} ${CX - 1.5},${CY - 72}`}
            fill="#c8a84b"
          />
        </g>

        {/* Minute hand — slim tapered */}
        <g transform={`rotate(${minuteDeg}, ${CX}, ${CY})`}>
          <polygon
            points={`${CX - 2},${CY + 20} ${CX + 2},${CY + 20} ${CX + 1},${CY - 98} ${CX - 1},${CY - 98}`}
            fill="#e8d5a3"
          />
        </g>

        {/* Second hand */}
        <g transform={`rotate(${secondDeg}, ${CX}, ${CY})`}>
          <line x1={CX} y1={CY + 24} x2={CX} y2={CY - 108}
            stroke="#e05c38" strokeWidth="1.2" strokeLinecap="round" />
          <circle cx={CX} cy={CY + 10} r="4" fill="#e05c38" />
        </g>

        {/* Center cap */}
        <circle cx={CX} cy={CY} r="5" fill="#c8a84b" />
        <circle cx={CX} cy={CY} r="2.5" fill="#060e1e" />
      </svg>
    </div>
  );
};

export default DarkNavy;
